import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { FileText, Trash2, Download, Printer, Eye, MapPin, Calendar, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function PlanogrammCard({ planogramm, bereichName, locationMap, canDelete, onDelete, onEdit, token, listMode }) {
  const [imgError, setImgError] = useState(false);
  const p = planogramm;

  const stationNames = (p.station_ids || [])
    .map(id => locationMap?.[id] || id)
    .join(', ');

  const createdDate = p.created_at
    ? new Date(p.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

  const gueltigAb = p.gueltig_ab
    ? new Date(p.gueltig_ab).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  const pdfUrl = `${API}/api/planogramm/${p.id}/pdf?token=${token}`;

  const openPdf = () => window.open(pdfUrl, '_blank');

  const downloadPdf = async (e) => {
    e.stopPropagation();
    try {
      const res = await fetch(pdfUrl);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${p.regal_name || p.titel || 'planogramm'}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  const printPdf = (e) => {
    e.stopPropagation();
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => { iframe.contentWindow.print(); };
    setTimeout(() => document.body.removeChild(iframe), 30000);
  };

  const thumbnailUrl = p.thumbnail_url
    ? `${API}${p.thumbnail_url}?token=${token}`
    : null;

  if (listMode) {
    return (
      <Card
        className="flex items-center gap-4 p-3 hover:bg-accent/50 transition-colors cursor-pointer"
        onClick={openPdf}
        data-testid={`planogramm-list-item-${p.id}`}
      >
        <div className="shrink-0 w-12 h-12 rounded bg-muted flex items-center justify-center overflow-hidden">
          {thumbnailUrl && !imgError ? (
            <img
              src={thumbnailUrl}
              alt=""
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <FileText className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{p.regal_name}</div>
          <div className="text-xs text-muted-foreground truncate">{p.titel}</div>
        </div>
        {bereichName && <Badge variant="outline" className="shrink-0 text-xs">{bereichName}</Badge>}
        <div className="text-xs text-muted-foreground shrink-0 hidden sm:block">{createdDate}</div>
        {canDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-primary"
            onClick={(e) => { e.stopPropagation(); onEdit?.(p); }}
            data-testid={`edit-planogramm-${p.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        {canDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
            data-testid={`delete-planogramm-${p.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground" onClick={downloadPdf} title="Herunterladen">
          <Download className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground" onClick={printPdf} title="Drucken">
          <Printer className="h-4 w-4" />
        </Button>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden group" data-testid={`planogramm-card-${p.id}`}>
      {/* Thumbnail */}
      <div
        className="aspect-[4/3] bg-muted flex items-center justify-center overflow-hidden cursor-pointer relative"
        onClick={openPdf}
      >
        {thumbnailUrl && !imgError ? (
          <img
            src={thumbnailUrl}
            alt={p.titel}
            className="w-full h-full object-contain"
            onError={() => setImgError(true)}
          />
        ) : (
          <FileText className="h-12 w-12 text-muted-foreground/50" />
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <Eye className="h-8 w-8 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        <div>
          <h3 className="font-semibold text-sm truncate">{p.regal_name}</h3>
          <p className="text-xs text-muted-foreground truncate">{p.titel}</p>
        </div>

        <div className="flex flex-wrap gap-1">
          {bereichName && (
            <Badge variant="secondary" className="text-xs">{bereichName}</Badge>
          )}
          {gueltigAb && (
            <Badge variant="outline" className="text-xs gap-1">
              <Calendar className="h-3 w-3" />
              ab {gueltigAb}
            </Badge>
          )}
        </div>

        {stationNames && (
          <div className="flex items-start gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
            <span className="line-clamp-2">{stationNames}</span>
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-muted-foreground">{createdDate}</span>
          <div className="flex gap-1">
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onEdit?.(p)}
                data-testid={`edit-planogramm-${p.id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={downloadPdf}
              title="Herunterladen"
              data-testid={`download-pdf-${p.id}`}
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={printPdf}
              title="Drucken"
              data-testid={`print-pdf-${p.id}`}
            >
              <Printer className="h-3.5 w-3.5" />
            </Button>
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={() => onDelete(p.id)}
                data-testid={`delete-planogramm-${p.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
